/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { BillingSettingsSchema, type BillingSettingsInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type BillingSettings } from '../../lib/api/billing-settings.js';
import { useUpdateBillingSettings } from '../../lib/queries/billing-settings.js';

import {
  type Ctrl,
  type Errs,
  NumberField,
  PaymentModesField,
  type Reg,
  ScaledField,
  SectionCard,
  TextField,
  ToggleRow,
} from './billing-settings-fields.js';

function ProformaSection({ register, errors }: { register: Reg; errors: Errs }): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('settings.billing.proformaCycle')}>
      <NumberField
        register={register}
        name="proformaDay"
        label={t('settings.billing.proformaDay')}
        min={24}
        max={28}
        error={errors.proformaDay?.message}
      />
      <ToggleRow
        register={register}
        name="autoSendProforma"
        label={t('settings.billing.autoSend')}
      />
      <ToggleRow
        register={register}
        name="autoConvertApproved"
        label={t('settings.billing.autoConvert')}
      />
    </SectionCard>
  );
}

function InvoiceSection({
  register,
  control,
  errors,
}: {
  register: Reg;
  control: Ctrl;
  errors: Errs;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('settings.billing.invoiceTax')}>
      <TextField
        register={register}
        name="invoicePrefix"
        label={t('settings.billing.invoicePrefix')}
        error={errors.invoicePrefix?.message}
      />
      <NumberField
        register={register}
        name="invoiceNextSeq"
        label={t('settings.billing.invoiceNextSeq')}
        min={1}
        max={99999999}
        error={errors.invoiceNextSeq?.message}
      />
      <TextField
        register={register}
        name="proformaPrefix"
        label={t('settings.billing.proformaPrefix')}
        error={errors.proformaPrefix?.message}
      />
      <ScaledField
        control={control}
        name="gstRateBps"
        label={t('settings.billing.gstRate')}
        suffix="%"
        error={errors.gstRateBps?.message}
      />
    </SectionCard>
  );
}

function PayRulesSection({ register, errors }: { register: Reg; errors: Errs }): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('settings.billing.payRules')}>
      <NumberField
        register={register}
        name="payPctCompleted"
        label={t('settings.billing.payCompleted')}
        min={0}
        max={100}
        suffix="%"
        error={errors.payPctCompleted?.message}
      />
      <NumberField
        register={register}
        name="payPctClientLate"
        label={t('settings.billing.payClientLate')}
        min={0}
        max={100}
        suffix="%"
        error={errors.payPctClientLate?.message}
      />
      <NumberField
        register={register}
        name="payPctConsultantAbsent"
        label={t('settings.billing.payConsultantAbsent')}
        min={0}
        max={100}
        suffix="%"
        error={errors.payPctConsultantAbsent?.message}
      />
      <NumberField
        register={register}
        name="payPctCancelled"
        label={t('settings.billing.payCancelled')}
        min={0}
        max={100}
        suffix="%"
        error={errors.payPctCancelled?.message}
      />
      <NumberField
        register={register}
        name="payPctNoShow"
        label={t('settings.billing.payNoShow')}
        min={0}
        max={100}
        suffix="%"
        error={errors.payPctNoShow?.message}
      />
    </SectionCard>
  );
}

function PaymentSection({ control, errors }: { control: Ctrl; errors: Errs }): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('settings.billing.payments')}>
      <PaymentModesField control={control} error={errors.acceptedPaymentModes?.message} />
      <ScaledField
        control={control}
        name="noShowFeePaise"
        label={t('settings.billing.noShowFee')}
        suffix="₹"
        error={errors.noShowFeePaise?.message}
      />
    </SectionCard>
  );
}

function SlaSection({ register, errors }: { register: Reg; errors: Errs }): JSX.Element {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('settings.billing.sla')}>
      <NumberField
        register={register}
        name="slaProformaDeclinedHours"
        label={t('settings.billing.slaProformaDeclined')}
        min={1}
        max={8760}
        suffix="h"
        error={errors.slaProformaDeclinedHours?.message}
      />
      <NumberField
        register={register}
        name="slaPatientQueryHours"
        label={t('settings.billing.slaPatientQuery')}
        min={1}
        max={8760}
        suffix="h"
        error={errors.slaPatientQueryHours?.message}
      />
      <NumberField
        register={register}
        name="slaInvoiceOverdueHours"
        label={t('settings.billing.slaInvoiceOverdue')}
        min={1}
        max={8760}
        suffix="h"
        error={errors.slaInvoiceOverdueHours?.message}
      />
      <NumberField
        register={register}
        name="slaConsultantInvoiceHours"
        label={t('settings.billing.slaConsultantInvoice')}
        min={1}
        max={8760}
        suffix="h"
        error={errors.slaConsultantInvoiceHours?.message}
      />
    </SectionCard>
  );
}

/** Admin → Settings → Billing & Rules. Validated by shared `BillingSettingsSchema`. */
export function BillingSettingsForm({
  settings,
}: {
  readonly settings: BillingSettings;
}): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateBillingSettings();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BillingSettingsInput>({
    resolver: zodResolver(BillingSettingsSchema),
    defaultValues: settings,
  });

  const submit = useCallback(
    async (values: BillingSettingsInput): Promise<void> => {
      await update.mutateAsync(values);
    },
    [update],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="max-w-4xl space-y-5" noValidate>
      <div className="grid gap-5 lg:grid-cols-2">
        <ProformaSection register={register} errors={errors} />
        <InvoiceSection register={register} control={control} errors={errors} />
        <PayRulesSection register={register} errors={errors} />
        <PaymentSection control={control} errors={errors} />
        <SlaSection register={register} errors={errors} />
      </div>

      {update.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      {update.isSuccess && (
        <p role="status" className="text-sm text-emerald-600">
          {t('settings.saved')}
        </p>
      )}

      <Button type="submit" disabled={isSubmitting || update.isPending}>
        {t('settings.save')}
      </Button>
    </form>
  );
}
